import { useEffect, useMemo, useRef, useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getCategoryProductList, getMenu } from "../services/apiService";
import noimage from "../assets/noimage.webp";
import "../styles/CategoriesPage.css";
import {
  AddItemToWishList,
  AddItemToCart,
  DeleteFromWishList,
  getProductQty,
} from "../services/Cart";
import { Notifyed } from "../services/Auth";
import { FaRegHeart, FaHeart } from "react-icons/fa";
import { showSnackbar } from "../comopnents/snackbar";
import LoadingSpinner from "../comopnents/LoadingSpinner";
// import { Notifyed } from "../services/Cart";
import { CartContext } from "../contextApi/CartContext";
import CartQuantityControl from "../comopnents/CartQuantityControl";
import PurchaseOptionsModal from "../comopnents/PurchaseOptionsModal";
import {
  buildCartPurchaseData,
  buildEnhancedPurchaseData,
  getMinimumPurchaseQuantity,
  getMinOrderQty,
  getPurchaseQuantityStep,
  getSavedCartPurchaseData,
  shouldShowPurchaseModal,
} from "../utils/purchaseOptions";
import { isOutOfStock } from "../utils/stock";


const getCategoryImage = (category) =>
  category?.Images?.find((image) => image?.imageurl)?.imageurl || noimage;

const BrandProducts = () => {
  const isLoggedIn = Boolean(localStorage.getItem("token"));
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const categoryScrollRef = useRef(null);
  const [selectedCategorySlug, setSelectedCategorySlug] = useState(
    searchParams.get("category") || "",
  );
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [wishlistedProducts, setWishlistedProducts] = useState({});
  const [wishlist, setWishlist] = useState({});
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [pendingPurchaseProduct, setPendingPurchaseProduct] = useState(null);

  const [cartItems, setCartItems] = useState([]);
  const {
    addToCart,
    deleteCartItems,
    itemList,
    updateQty,
    setCartQty,
    userId,
    removeFromCart,
  } = useContext(CartContext);



  const handleAddToCart = async (product, purchaseType, couponData = null) => {
    const userId = localStorage.getItem("userId");
    if (isOutOfStock(product)) {
      await handleNotifyMe(product.productid || product.dtl?.[0]?.productid);
      return;
    }

    const purchaseData = buildEnhancedPurchaseData(product, purchaseType, couponData);
    const productId = purchaseData.productId;

    if (!userId) {
      showSnackbar("info", "Please login to add items to cart");
      navigate("/login");
      return;
    }

    try {
      const payload = {
        customerid: userId,
        productid: productId,
        qty: purchaseData.quantity,
        stock: product.stock || product.dtl?.[0]?.stock,
      };
      await AddItemToCart(payload); // API call
      addToCart(productId, purchaseData.quantity, purchaseData); // Update frontend cart state
      showSnackbar("success", "Item added to cart!");
    } catch (error) {
      console.error(
        "Add to cart error:",
        error.response?.data || error.message,
      );
      showSnackbar("error", "Failed to add item to cart!");
    }
  };

  const openPurchaseModal = (product) => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      showSnackbar("info", "Please login to add items to cart");
      navigate("/login");
      return;
    }

    if (!shouldShowPurchaseModal(product)) {
      handleAddToCart(product, "piece");
      return;
    }
    setPendingPurchaseProduct(product);
    setShowPurchaseModal(true);
  };

  const confirmPurchaseType = async (purchaseType, couponData) => {
    if (!pendingPurchaseProduct) return;

    await handleAddToCart(pendingPurchaseProduct, purchaseType, couponData);
    setShowPurchaseModal(false);
    setPendingPurchaseProduct(null);
  };

  const handleNotifyMe = async (productid) => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      showSnackbar("info", "Please login to receive notifications");
      navigate("/login");
      return;
    }

    try {
      await Notifyed({ customerid: userId, productid });
      showSnackbar("success", "We will notify you when product is available");
    } catch (error) {
      console.error("Notify me error:", error.response?.data || error.message);
      showSnackbar("error", "Failed to register notify request");
    }
  };

  const handleCartQtyChange = async (productId, newQty) => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      showSnackbar("info", "Please login to update cart quantity");
      navigate("/login");
      return;
    }

    try {
      await getProductQty({ user_id: userId, productid: productId, qty: newQty });
      setCartQty(productId, newQty);
    } catch (error) {
      console.error("API Error:", error?.message || error);
      showSnackbar("error", "Failed to update quantity");
    }
  };

  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        const data = await getMenu();
        const menuCategories = Array.isArray(data?.Data) ? data.Data : [];
        setCategories(menuCategories);

        if (!selectedCategorySlug && menuCategories.length) {
          const firstSlug =
            menuCategories[0]?.Images?.[0]?.category_slug ||
            menuCategories[0]?.category;
          setSelectedCategorySlug(firstSlug);
        }
      } catch (error) {
        console.error("Failed to load categories:", error);
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, []);

  const selectedCategory = useMemo(() => {
    if (!categories.length) return null;

    return (
      categories.find((category) => {
        const slug = category?.Images?.[0]?.category_slug || category?.category;
        return slug === selectedCategorySlug || category?.category === selectedCategorySlug;
      }) || categories[0]
    );
  }, [categories, selectedCategorySlug]);

  useEffect(() => {
    if (!selectedCategory?.category) return;
    
    const loadProducts = async () => {
      try {
        setProductsLoading(true);
        const data = await getCategoryProductList(selectedCategory.category);
        setProducts(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        console.error("Failed to load category products:", error);
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    };

    loadProducts();
  }, [selectedCategory?.category]);

  const AddToWishList = async (productid) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        showSnackbar("info", "Please login to add items to wishlist");
        navigate("/login");
        return;
      }

      if (wishlistItems.includes(productid)) return;

      await AddItemToWishList({ productid, customerid: userId });
      setWishlistItems((prev) => [...prev, productid]);
      showSnackbar("success", "Added to wishlist!");
    } catch (error) {
      console.error("Wishlist error:", error.response?.data || error.message);
    }
  };


  const handleDeleteWishList = async (productid) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      await DeleteFromWishList({ customerid: userId, productid });
      setWishlist((prev) => ({ ...prev, [productid]: false }));
    } catch (err) {
      console.error("Error deleting from wishlist:", err);
    }
  };



  const handleCategorySelect = (category) => {
    const slug = category?.Images?.[0]?.category_slug || category?.category;
    setSelectedCategorySlug(slug);
    setSearchParams({ category: slug });
  };


  const scrollCategories = (direction) => {
    if (!categoryScrollRef.current) return;

    categoryScrollRef.current.scrollBy({
      left: direction * 260,
      behavior: "smooth",
    });
  };


  return (
    <main className="categories-page">
      <section className="categories-header">
        <h1>CATEGORIES</h1>
      </section>

      {loading ? (
        <div className="categories-loading">
          <LoadingSpinner text="Loading categories..." />
        </div>
      ) : (
        <>
          <div className="category-page-slider-container">
            <button
              type="button"
              className="category-page-arrow"
              onClick={() => scrollCategories(-1)}
              aria-label="Scroll categories left"
            >

            </button>

            <div
              className="category-scroll"
              aria-label="Product categories"
              ref={categoryScrollRef}
             >
              {categories.map((category) => {
                const slug = category?.Images?.[0]?.category_slug || category?.category;
                const isActive =
                  selectedCategory?.category === category?.category ||
                  selectedCategorySlug === slug;

                return (
                  <button
                    type="button"
                    className={`category-scroll-button ${isActive ? "active" : ""}`}
                    key={slug}
                    onClick={() => handleCategorySelect(category)}
                  >
                    {/* <img
                      src={getCategoryImage(category)}
                      alt={category.category}
                      onError={(event) => {
                        event.currentTarget.src = noimage;
                      }}
                    /> */}
                    <span>{category.category}</span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              className="category-page-view-all-btn"
              onClick={() => navigate("/shop?category=all")}
            >
              View All
            </button>

            {/* <button
              type="button"
              className="category-page-arrow"
              onClick={() => scrollCategories(1)}
              aria-label="Scroll categories right"
            >
              
            </button> */}
          </div>
          <div>

          </div>

          <section className="category-products-panel">
            <div className="selected-category-card">
              <img
                src={getCategoryImage(selectedCategory)}
                alt={selectedCategory?.category || "Category"}
                onError={(event) => {
                  event.currentTarget.src = noimage;
                }}
              />
              <div>
                <span>Selected Category</span>
                <h2>{selectedCategory?.category}</h2>
              </div>
            </div>

            <div className="category-products-content">
              <div className="category-products-title">
                <h3>{selectedCategory?.category} Products</h3>
                <span>{products.length} items</span>
              </div>

              {productsLoading ? (
                <div className="category-products-loading">
                  <LoadingSpinner text="Loading products..." />
                </div>
              ) : products.length ? (
                <div className="category-products-grid">
                  {products.map((product) => {
                    const imageUrl =
                      product.image1?.trim() ||
                      product.image2?.trim() ||
                      product.image3?.trim() ||
                      product.dtl?.[0]?.image1?.trim() ||
                      noimage;
                    const mrp = Number(product.mrp || product.dtl?.[0]?.mrp || 0);
                    const discount = Number(product.discount || 0);
                    const finalPrice = discount
                      ? Math.round(mrp - (mrp * discount) / 100)
                      : mrp;
                    const qty =
                      itemList.find(
                        (item) => String(item.productId) === String(product.productid),
                      )?.count || 0;
                    const minorderqty = product.minorderqty || product.dtl?.[0]?.minorderqty || 1;
                    const stock = product.stock || product.dtl?.[0]?.stock || 0;
                    const netrate = Number(product.netrate || product.dtl?.[0]?.netrate || 0);
                    const savedPurchaseData = getSavedCartPurchaseData(product.productid);
                    const purchaseType = savedPurchaseData?.purchaseType || "piece";

                    return (
                      <div
                        className="category-product-card"
                        key={product.productid}
                      >
                        <button
                          type="button"
                          className="category-product-image-btn"
                          onClick={() =>
                            navigate(`/productdetail?productid=${product.productid}`)
                          }
                        >
                          {isLoggedIn && ((stock > 0 && netrate > 0) || discount > 0) && (
                            <span className="category-product-discount">
                              {stock > 0 && netrate > 0 ? "Netrate" : `${discount}% OFF`}
                            </span>
                          )}
                          <div className="bestsellers-actions">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setWishlist((prev) => {
                                  const updated = !prev[product.productid];
                                  if (updated) AddToWishList(product.productid);
                                  else handleDeleteWishList(product.productid);
                                  return { ...prev, [product.productid]: updated };
                                });
                              }}
                              aria-label={wishlist[product.productid] ? "Remove from wishlist" : "Add to wishlist"}
                              type="button"
                            >
                              {wishlist[product.productid] ? (
                                <FaHeart color="red" />
                              ) : (
                                <FaRegHeart />
                              )}
                            </button>
                          </div>

                          <img
                            src={imageUrl}
                            alt={product.modelno || "Product"}
                            onError={(event) => {
                              event.currentTarget.src = noimage;
                            }}
                          />
                        </button>

                        <span className="category-product-brand">
                          {product.brand || selectedCategory?.category}
                        </span>

                        <strong>
                          {product.modelno || product.displaymodelno}
                        </strong>

                        <small>
                          {product.subcategory || product.category}
                        </small>

                        {mrp > 0 && (
                          <span className="category-product-price-row">
                            {!isLoggedIn && (
                              <span
                                className="category-product-mrp"
                                style={{ textDecoration: "none" }}
                              >
                                MRP {Number(mrp || 0).toFixed(2)}
                              </span>
                            )}

                            {isLoggedIn && netrate <= 0 && (
                              <span className="category-product-mrp">
                                MRP {Number(mrp || 0).toFixed(2)}
                              </span>
                            )}

                            {isLoggedIn && (
                              <span className="category-product-price">
                                Rs. {Number(netrate > 0 ? netrate : finalPrice || 0).toFixed(2)}
                              </span>
                            )}
                          </span>
                        )}
                        {stock <= 0 ? (
                          <button
                            style={{
                              width: "100%",
                              padding: "7px",
                              borderRadius: "25px",
                              border: "none",
                              fontWeight: "600",
                              backgroundColor: "#e0e0e0",
                              color: "#777777",
                              transition: "background-color 0.3s ease",
                            }}
                            onClick={() => handleNotifyMe(product.productid)}
                          >
                            Notify Me
                          </button>
                        ) : qty > 0 ? (
                          <CartQuantityControl
                            qty={qty}
                            minQty={getMinimumPurchaseQuantity(product, purchaseType)}
                            step={purchaseType !== "piece" ? getPurchaseQuantityStep(product, purchaseType) : undefined}
                            stock={stock}
                            purchaseType={purchaseType}
                            product={product}
                            onChange={(newQty) =>
                              handleCartQtyChange(product.productid, newQty)
                            }
                            className="qty-box"
                          />
                        ) : (
                          <button
                            style={{
                              width: "100%",
                              padding: "7px",
                              borderRadius: "25px",
                              border: "none",
                              fontWeight: "600",
                              backgroundColor: "#005487",
                              color: "#ffffff",
                              transition: "background-color 0.3s ease",
                            }}
                            onClick={() => openPurchaseModal(product)}
                          >
                            Add to Cart
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="category-products-empty">
                  No products found for this category.
                </div>
              )}
            </div>
          </section>
        </>
      )}
      <PurchaseOptionsModal
        isOpen={showPurchaseModal}
        product={pendingPurchaseProduct}
        onClose={() => {
          setShowPurchaseModal(false);
          setPendingPurchaseProduct(null);
        }}
        onConfirm={confirmPurchaseType}
      />
    </main>
  );
};

export default BrandProducts;
